import React, {
  useState,
  RefObject,
  Fragment,
  useEffect,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { styled } from 'dripsy';

import { CommentList } from '@models/Comments/CommentListModel';
import { parseApiError, passIf } from '@utils';
import { AnalyticsService, ToastService } from '@services';
import {
  ViewerTextInput,
  ViewerTextInputRefType,
} from '../views/ViewerTextInput';
import { Button, ButtonStyleEnum } from '../common/Button';
import { Separator } from '../common/Separator';
import { IconTouchable } from '../common/IconTouchable';
import { Comment } from '@models/Comments/CommentModel';

export enum CommentInputSizeEnum {
  Small = 'small',
  Large = 'large',
}

export enum CommentInputModeEnum {
  Create = 'create',
  Update = 'update',
}

const InputRowContainer = styled(View)({
  flexDirection: 'row',
});

const InputContainer = styled(View)({
  flex: 1,
});

const ButtonsContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'flex-end',
});

const ButtonContainer = styled(View)({
  width: 180,
});

export type CommentInputTargetType =
  | { entity: CommentList; mode: CommentInputModeEnum.Create }
  | { entity: Comment; mode: CommentInputModeEnum.Update };

export interface ICommentInputProps {
  target: CommentInputTargetType;
  onSubmit?(): void;
  onCancel?(): void;
  inputRef?: RefObject<ViewerTextInputRefType>;
  size?: CommentInputSizeEnum;
}

export const CommentInput = observer<ICommentInputProps>(
  ({ target, onSubmit, inputRef, size, onCancel, ...props }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');

    useEffect(() => {
      if (
        target.entity &&
        target.mode === CommentInputModeEnum.Update
      ) {
        setText(target.entity.message);
      } else if (
        !target.entity &&
        target.mode === CommentInputModeEnum.Create
      ) {
        clearText();
      }
    }, [target.entity, target.mode]);

    async function submit() {
      try {
        clearText();

        if (onSubmit) {
          onSubmit();
        }

        if (target.mode === CommentInputModeEnum.Update) {
          await target.entity.update.run({ message: text });

          ToastService.showSuccess(
            t('common.commentInput.successMessageUpdate'),
          );
        } else {
          await target.entity.create.run({ message: text });

          AnalyticsService.trackPostComment(
            target.entity.parentEntityName,
          );

          ToastService.showSuccess(
            t('common.commentInput.successMessageAdd'),
          );
        }
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function clearText() {
      setText('');
    }

    function cancel() {
      if (onCancel) {
        onCancel();
      } else {
        setText('');
      }
    }

    const isLarge = size === CommentInputSizeEnum.Large;
    const isValidComment = text.trim().length > 0;

    const isUpdate = target.mode === CommentInputModeEnum.Update;
    const placeholder = isUpdate
      ? t('common.commentInput.updateYourComment')
      : t('common.commentInput.addYourComment');
    const submitText = isUpdate
      ? Platform.select({
          web: t('common.commentInput.updateComment'),
          default: t('common.commentInput.update'),
        })
      : Platform.select({
          web: t('common.commentInput.addComment'),
          default: t('common.commentInput.add'),
        });

    return (
      <View>
        <InputRowContainer>
          <InputContainer>
            <ViewerTextInput
              ref={inputRef}
              value={text}
              onChangeText={setText}
              isValid={isValidComment}
              onSubmit={passIf(!isLarge, submit)}
              maxLength={1000}
              maxHeight={100}
              placeholder={placeholder}
              submitText={submitText}
              {...props}
            />
          </InputContainer>
          {!isLarge && onCancel && (
            <Fragment>
              <Separator size="$5" vertical />
              <IconTouchable
                name="close-light"
                color="$text400"
                onPress={onCancel}
              />
            </Fragment>
          )}
        </InputRowContainer>
        {isLarge && (
          <Fragment>
            <Separator size="$3" />
            <ButtonsContainer>
              <ButtonContainer>
                <Button
                  buttonStyle={ButtonStyleEnum.Outline}
                  onPress={cancel}
                >
                  {t('common.commentInput.cancel')}
                </Button>
              </ButtonContainer>
              <Separator size="$6" vertical />
              <ButtonContainer>
                <Button disabled={!isValidComment} onPress={submit}>
                  {submitText}
                </Button>
              </ButtonContainer>
            </ButtonsContainer>
          </Fragment>
        )}
      </View>
    );
  },
);
